import { Button, Dialog, DialogActions, DialogContent, FormControl, TextField } from "@material-ui/core";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";

import { AjaxResult } from "../../../../../enums/ajaxResult";
import { SiteCommissioningSummary } from "../../../../../dtos/siteCommissioning";
import { FormField } from "../../../../shared/formField";
import { FormSection } from "../../../../shared/formSection";
import { ValidationErrors } from "../../../../shared/validationErrors";
import { useApi } from "../../../../useApi";

import { isPositiveInteger, isValidIPv4 } from "./validation";

interface Props {
    open: boolean;
    onClose: () => void;
    siteId: number;
    fields: Record<string, unknown>;
    onSaved: (summary: SiteCommissioningSummary) => void;
}

export const EditHmiConfigurationDialog: React.FC<Props> = ({ open, onClose, siteId, fields, onSaved }) => {
    const { siteApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const [ipAddress, setIpAddress] = useState("");
    const [port, setPort] = useState("");
    const [errors, setErrors] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setIpAddress(typeof fields.ipAddress === "string" ? fields.ipAddress : "");
            setPort(fields.port !== undefined && fields.port !== null ? String(fields.port) : "");
            setErrors([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const save = () => {
        const clientErrors: string[] = [];
        if (!isValidIPv4(ipAddress)) {
            clientErrors.push("HMI IP address must be a valid IPv4 address.");
        }
        if (!isPositiveInteger(port)) {
            clientErrors.push("HMI port is required and must be a positive number.");
        }
        if (clientErrors.length > 0) {
            setErrors(clientErrors);
            return;
        }

        const { model: _deviceReportedModel, ...editableFields } = fields;

        setSaving(true);
        siteApi
            .updateCommissioning(siteId, [
                { name: "hmiConfiguration", items: { config: { fields: { ...editableFields, ipAddress, port: Number(port) } } } },
            ])
            .then((r) => {
                setSaving(false);
                if (r.result === AjaxResult.Success && r.data) {
                    onSaved(r.data);
                    onClose();
                    enqueueSnackbar("HMI configuration updated", { variant: "success" });
                } else if (r.messages) {
                    setErrors(r.messages);
                } else {
                    enqueueSnackbar("ERROR: Failed to update HMI configuration", { variant: "error" });
                }
            });
    };

    return (
        <Dialog open={open} disablePortal>
            <DialogContent style={{ minWidth: "400px" }}>
                <FormSection headerText="Edit HMI Configuration">
                    <FormField label="IP Address" required>
                        <FormControl style={{ flexGrow: 1 }}>
                            <TextField
                                value={ipAddress}
                                onChange={(event) => setIpAddress(event.currentTarget.value)}
                                variant="outlined"
                                InputProps={{ inputProps: { style: { padding: "8px" } } }}
                            />
                        </FormControl>
                    </FormField>
                    <FormField label="Port" required>
                        <FormControl style={{ flexGrow: 1 }}>
                            <TextField
                                value={port}
                                onChange={(event) => setPort(event.currentTarget.value)}
                                variant="outlined"
                                InputProps={{ inputProps: { style: { padding: "8px" } } }}
                            />
                        </FormControl>
                    </FormField>
                    <ValidationErrors errors={errors} />
                </FormSection>
            </DialogContent>
            <DialogActions>
                <Button color="primary" variant="contained" onClick={save} disabled={saving}>
                    {saving ? "Saving..." : "Save"}
                </Button>
                <Button color="secondary" variant="contained" onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};
